import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  getCity,
  getCountry,
  getState,
  getStreetAddress,
  getStreetAddressLine2,
  getZipCode,
} from "components/AddressComponent/AddressComponentFieldValues";
import { CustomGoogleSuggestedOption } from "components/AddressComponent/AddressComponentOptions";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Input from "components/DesignSystem/Input/Input";
import { PlusIcon } from "components/icons/PlusIcon";
import { getIn, setIn, useFormikContext } from "formik";
import { ReactNode, useEffect, useRef, useState } from "react";
import {
  components as reactSelectComponents,
  MenuListProps,
  MultiValue,
  SingleValue,
} from "react-select";
import { useGetCountriesDataQuery } from "store/apis/onboarding";
import { Countries } from "types/Models/countries";
import { debounce } from "utils/debouncing";
import { getGroupedCountryOptionsByName } from "utils/stateRequired";

export type AddressFieldValues = {
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
};

type ParsedAddress = AddressFieldValues;

type AddressFieldKey = keyof AddressFieldValues;

export const EMPTY_ADDRESS_FIELDS: AddressFieldValues = {
  streetAddress: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "",
  zipcode: "",
};

const ADDRESS_FIELD_KEYS: AddressFieldKey[] = [
  "streetAddress",
  "addressLine2",
  "city",
  "state",
  "country",
  "zipcode",
];

const API_KEY = process.env.PUBLIC_GOOGLE_MAP_KEY;

const OrDivider = () => (
  <div className="t-flex t-items-center t-gap-2">
    <div className="t-h-px t-flex-1 t-bg-neutral-10" />
    <span className="t-text-body-sm t-text-text-30">or</span>
    <div className="t-h-px t-flex-1 t-bg-neutral-10" />
  </div>
);

const AddManuallyLink = ({
  query,
  onClick,
  showIcon = false,
}: {
  query: string;
  onClick: () => void;
  showIcon?: boolean;
}) => (
  <Button
    type="button"
    customType="link"
    size="small"
    leftIcon={showIcon ? <PlusIcon size="12" /> : undefined}
    onClick={onClick}
  >
    Add &quot;{query}&quot; manually
  </Button>
);

const SuggestionsMenuList = ({
  children,
  ...props
}: MenuListProps<OptionData>) => (
  <reactSelectComponents.MenuList {...props}>
    {children}
    {/* @ts-expect-error — actions is a custom prop forwarded to Select, see Combobox */}
    {props.selectProps?.actions && (
      <div className="t-py-2 t-px-3 t-sticky t-bottom-0 t-bg-white">
        {/* @ts-expect-error — actions is a custom prop forwarded to Select, see Combobox */}
        {props.selectProps?.actions}
      </div>
    )}
  </reactSelectComponents.MenuList>
);

const AddressNoOptionsMessage = ({
  query,
  onEnterManually,
}: {
  query: string;
  onEnterManually: () => void;
}) => {
  if (!query) {
    return (
      <div className="t-p-3 t-px-2 t-text-body-sm t-text-text-30">
        Type an address to search...
      </div>
    );
  }

  return (
    <div className="t-p-3 t-px-2 t-flex t-items-center t-gap-1 t-flex-wrap">
      <span className="t-text-body-sm t-text-text-30">No address found.</span>
      <AddManuallyLink query={query} onClick={onEnterManually} />
    </div>
  );
};

const AddressSearch = ({
  label,
  required,
  onPick,
  countries,
  onEnterManually,
}: {
  label?: ReactNode;
  required?: boolean;
  onPick: (address: ParsedAddress) => void;
  countries: Countries[];
  onEnterManually: () => void;
}) => {
  const places = useMapsLibrary("places");
  const tokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  );
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (places?.AutocompleteSessionToken) {
      tokenRef.current = new places.AutocompleteSessionToken();
    }
  }, [places]);

  const fetchSuggestions = async (input: string) => {
    if (!places || !input) {
      setSuggestions([]);
      return;
    }
    try {
      setIsLoading(true);
      const { suggestions: results } =
        await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          language: "en-US",
          sessionToken: tokenRef.current ?? undefined,
        });
      setSuggestions(results);
    } catch {
      setSuggestions([]);
    }
    setIsLoading(false);
  };

  const debouncedFetch = debounce(fetchSuggestions);

  const handleSelect = async (
    option: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    const selected = option as SingleValue<OptionData>;
    if (!places || !selected?.value) return;
    const place = new places.Place({ id: selected.value });
    const info = await place.fetchFields({ fields: ["addressComponents"] });
    onPick({
      streetAddress: getStreetAddress(info.place) ?? "",
      addressLine2: getStreetAddressLine2(info.place) ?? "",
      city: getCity(info.place) ?? "",
      state: getState(info.place, countries) ?? "",
      country: getCountry(info.place, countries) ?? "",
      zipcode: getZipCode(info.place) ?? "",
    });
    setSuggestions([]);
  };

  const options = suggestions
    .map((suggestion) => ({
      value: suggestion.placePrediction?.toPlace().id,
      label: suggestion.placePrediction?.text.text,
    }))
    .filter(({ value }) => value);

  const handleInputChange = (value: string) => {
    setQuery(value);
    void debouncedFetch(value);
  };

  const handleMenuOpen = () => setIsMenuOpen(true);

  const handleMenuClose = () => {
    setIsMenuOpen(false);
    setSuggestions([]);
  };

  const hasSuggestions = options.length > 0;

  return (
    <Combobox
      label={label}
      required={required}
      tooltip
      tooltipText="The principal business address on file with the IRS"
      placeholder={isMenuOpen ? "" : "Type an address to search..."}
      isLoading={isLoading}
      filterOption={() => true}
      onInputChange={handleInputChange}
      onChange={handleSelect}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      menuPortalTarget={document.body}
      // @ts-expect-error — option values are place ids
      options={options}
      actions={
        hasSuggestions ? (
          <div className="t-flex t-flex-col t-gap-2">
            <OrDivider />
            <AddManuallyLink query={query} onClick={onEnterManually} showIcon />
          </div>
        ) : undefined
      }
      components={{
        Option: (props) => <CustomGoogleSuggestedOption {...props} />,
        NoOptionsMessage: () => (
          <AddressNoOptionsMessage
            query={query}
            onEnterManually={onEnterManually}
          />
        ),
        MenuList: SuggestionsMenuList,
      }}
    />
  );
};

export const AddressPicker = ({
  label,
  required = true,
  name,
}: {
  label?: ReactNode;
  required?: boolean;
  /** Formik path the fields live under, e.g. "address" → "address.city" */
  name?: string;
}) => {
  const { values, setFieldValue, setValues } =
    useFormikContext<Record<string, unknown>>();
  const { data: countries = [] } = useGetCountriesDataQuery();

  const [mode, setMode] = useState<"search" | "manual">("search");
  const autoRevealed = useRef(false);

  const fieldName = (key: AddressFieldKey) => (name ? `${name}.${key}` : key);

  const countryOptions = getGroupedCountryOptionsByName(countries);
  const selectedCountry = getIn(values, fieldName("country")) as string;
  const selectedState = getIn(values, fieldName("state")) as string;
  const streetAddress = getIn(values, fieldName("streetAddress"));

  useEffect(() => {
    if (!autoRevealed.current && streetAddress) {
      autoRevealed.current = true;
      setMode("manual");
    }
  }, [streetAddress]);

  const stateOptions =
    countries
      .find((country) => country.name === selectedCountry)
      ?.states?.map((state) => ({ value: state.name, label: state.name })) ??
    [];

  const handleCountryChange = (option: OptionData | null) => {
    setFieldValue(fieldName("country"), option?.value ?? "");
    setFieldValue(fieldName("state"), "");
  };

  const fillAddress = (address: ParsedAddress) => {
    const filledValues = ADDRESS_FIELD_KEYS.reduce(
      (acc, key) => setIn(acc, fieldName(key), address[key]),
      values
    );
    setValues(filledValues, true);
    setMode("manual");
  };

  return (
    <div className="t-flex t-flex-col t-gap-3">
      {mode === "search" ? (
        <div className="t-flex t-flex-col t-gap-1">
          <APIProvider apiKey={API_KEY || ""}>
            <AddressSearch
              label={label}
              required={required}
              onEnterManually={() => setMode("manual")}
              onPick={fillAddress}
              countries={countries}
            />
          </APIProvider>
        </div>
      ) : (
        <>
          <Input
            withForm
            name={fieldName("streetAddress")}
            label="Street address"
            placeholder="2100 Geng Rd"
            required={required}
          />
          <Input
            withForm
            name={fieldName("addressLine2")}
            label="Apartment, suite, etc. (optional)"
            placeholder="Suite 200"
          />
          <Combobox
            withForm
            name={fieldName("country")}
            label="Country"
            placeholder="Select country"
            required={required}
            menuPortalTarget={document.body}
            options={countryOptions}
            value={
              selectedCountry
                ? { label: selectedCountry, value: selectedCountry }
                : null
            }
            onChange={(option) =>
              handleCountryChange(option as OptionData | null)
            }
          />
          <Combobox
            withForm
            name={fieldName("state")}
            label="State"
            placeholder="Select state"
            required={required}
            menuPortalTarget={document.body}
            options={stateOptions}
            value={
              selectedState
                ? { label: selectedState, value: selectedState }
                : null
            }
          />
          <div className="t-grid t-grid-cols-2 t-gap-3">
            <Input
              withForm
              name={fieldName("city")}
              label="City"
              placeholder="Palo Alto"
              required={required}
            />
            <Input
              withForm
              name={fieldName("zipcode")}
              label="Zipcode"
              placeholder="94303"
              required={required}
            />
          </div>
          <div>
            <Button
              type="button"
              customType="link"
              size="small"
              onClick={() => setMode("search")}
            >
              Search address instead
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
